import React, { useEffect, useState } from "react";
import { Receipt, Download, Eye, Share2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand";
import {
  ReceiptViewerModal,
  buildReceiptFilename,
  buildPublicPdfUrl,
  buildWhatsAppShare,
} from "@/components/receipts/ReceiptViewerModal";
import { ContactPickerDialog } from "@/components/shared/ContactPickerDialog";
import { buildContactOptions } from "@/lib/contactActions";

/**
 * Receipts list with View / Download / Share actions.
 * Pass exactly one of:  paymentId | recurringGroupId | bookingId | enrollmentId
 *
 * - View   → opens an in-app PDF viewer modal (pdf.js) — no Chrome blocking.
 * - Download → fetches public PDF and saves with `<Name>_<ReceiptNumber>.pdf` filename.
 * - Share  → opens WhatsApp directly to the customer with a prefilled receipt link.
 */
export const ReceiptsList = ({
  paymentId,
  recurringGroupId,
  bookingId,
  enrollmentId,
  className = "",
  emptyHint = "Receipts will appear here after the first payment is recorded.",
  refreshKey = 0,
}) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [viewerReceipt, setViewerReceipt] = useState(null);
  const [pickerReceipt, setPickerReceipt] = useState(null);
  const { default_country_code } = useBrand();

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (paymentId) params.set("payment_id", paymentId);
    else if (recurringGroupId) params.set("recurring_group_id", recurringGroupId);
    else if (bookingId) params.set("booking_id", bookingId);
    else if (enrollmentId) params.set("enrollment_id", enrollmentId);
    else { setLoading(false); return; }

    setLoading(true);
    api.get(`/receipts?${params.toString()}`)
      .then((r) => { if (!cancelled) setReceipts(r.data.receipts || []); })
      .catch(() => { if (!cancelled) setReceipts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [paymentId, recurringGroupId, bookingId, enrollmentId, refreshKey]);

  const handleView = (rcpt) => setViewerReceipt(rcpt);

  const handleDownload = async (rcpt) => {
    setBusyId(rcpt.receipt_id);
    try {
      const res = await fetch(buildPublicPdfUrl(rcpt));
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildReceiptFilename(rcpt);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Download failed. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const openWhatsApp = (rcpt, overridePhone) => {
    const { waUrl, phone } = buildWhatsAppShare({
      receipt: rcpt,
      defaultCountryCode: default_country_code,
      overridePhone,
    });
    if (!phone) {
      toast.error("Customer mobile number unavailable.");
      return;
    }
    // Synchronous open keeps user activation alive (Chrome won't block).
    const win = window.open(waUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.message("Pop-up was blocked", {
        description: "Tap below to open WhatsApp.",
        action: {
          label: "Open WhatsApp",
          onClick: () => window.open(waUrl, "_blank", "noopener,noreferrer"),
        },
      });
    }
  };

  const handleShare = (rcpt) => {
    const recipient = rcpt?.snapshot?.recipient || {};
    // Apr 2026 — prefer LIVE phone numbers from users master (added by backend
    // as `current_phone` / `current_parent_phone`). Falls back to snapshot
    // for legacy rentals + customer receipts that have no user_id link.
    const studentPhone = rcpt?.current_phone || recipient.phone || "";
    const parentPhone = rcpt?.current_parent_phone || recipient.parent_phone || "";
    const guardianName = rcpt?.current_parent_name || recipient.parent_name || "";
    if (!parentPhone && !studentPhone) {
      toast.error("Customer mobile number unavailable.");
      return;
    }
    // Picker only when BOTH numbers exist (and are usable). Single-contact
    // case sends straight to whichever exists — matches every other flow.
    const opts = buildContactOptions({
      studentName: recipient.name,
      studentPhone,
      // The receipt snapshot doesn't carry parent_name (legacy schema).
      // Falls back to the role label "Parent/Guardian" inside the picker.
      guardianName,
      guardianPhone: parentPhone,
    });
    if (opts.length <= 1) {
      openWhatsApp(rcpt, parentPhone || studentPhone);
      return;
    }
    setPickerReceipt(rcpt);
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-xs text-slate-500 py-2 ${className}`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading receipts…
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <div className={`text-xs text-slate-500 italic ${className}`} data-testid="receipts-empty">
        {emptyHint}
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-2 ${className}`} data-testid="receipts-list">
        {receipts.map((r) => {
          const txn = r.snapshot?.payment_summary || {};
          const cur = r.snapshot?.currency_symbol || "₹";
          const dateStr = r.transaction_date_iso
            ? format(new Date(r.transaction_date_iso), "dd MMM yyyy")
            : "—";
          const isBusy = busyId === r.receipt_id;
          return (
            <div
              key={r.receipt_id}
              data-testid={`receipt-row-${r.receipt_number}`}
              className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-violet-100 bg-violet-50/40"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Receipt className="w-4 h-4 text-violet-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{r.receipt_number}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {cur}{txn.transaction_amount ?? r.transaction_amount ?? "—"} · {dateStr} · {(r.transaction_method || txn.payment_method || "—").toString().toUpperCase()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm" variant="ghost"
                  className="h-8 px-2 text-violet-700 hover:bg-violet-100"
                  onClick={() => handleView(r)}
                  disabled={isBusy}
                  data-testid={`receipt-view-${r.receipt_number}`}
                  title="View"
                >
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm" variant="ghost"
                  className="h-8 px-2 text-violet-700 hover:bg-violet-100"
                  onClick={() => handleDownload(r)}
                  disabled={isBusy}
                  data-testid={`receipt-download-${r.receipt_number}`}
                  title="Download"
                >
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm" variant="ghost"
                  className="h-8 px-2 text-violet-700 hover:bg-violet-100"
                  onClick={() => handleShare(r)}
                  disabled={isBusy}
                  data-testid={`receipt-share-${r.receipt_number}`}
                  title="Share via WhatsApp"
                >
                  <Share2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <ReceiptViewerModal
        open={!!viewerReceipt}
        onOpenChange={(o) => { if (!o) setViewerReceipt(null); }}
        receipt={viewerReceipt}
        defaultCountryCode={default_country_code}
      />

      <ContactPickerDialog
        open={!!pickerReceipt}
        onOpenChange={(o) => { if (!o) setPickerReceipt(null); }}
        title="Send receipt via WhatsApp"
        description={
          pickerReceipt?.snapshot?.recipient?.name
            ? `Choose who receives the receipt for ${pickerReceipt.snapshot.recipient.name}.`
            : "Choose who receives the receipt."
        }
        options={buildContactOptions({
          studentName: pickerReceipt?.snapshot?.recipient?.name,
          studentPhone: pickerReceipt?.current_phone || pickerReceipt?.snapshot?.recipient?.phone,
          guardianName: pickerReceipt?.current_parent_name || pickerReceipt?.snapshot?.recipient?.parent_name || "",
          guardianPhone: pickerReceipt?.current_parent_phone || pickerReceipt?.snapshot?.recipient?.parent_phone,
        })}
        onPick={(key) => {
          const phone = key === "student"
            ? (pickerReceipt?.current_phone || pickerReceipt?.snapshot?.recipient?.phone)
            : (pickerReceipt?.current_parent_phone || pickerReceipt?.snapshot?.recipient?.parent_phone);
          openWhatsApp(pickerReceipt, phone);
        }}
      />
    </>
  );
};

export default ReceiptsList;
